import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../../firebase-client';
import ErrorMessage from '../formComponents/ErrorMessage';
import SubmitButton from '../formComponents/SubmitButton';

function createEmptyDiscountRule(index = 0) {
  return {
    id: `membership_discount_${Date.now()}_${index + 1}`,
    name: '',
    status: 'active',
    type: 'fixed',
    amount: '',
    duration: 'credit',
    code: '',
    usageLimitTotal: '',
    usageLimitPerMember: '',
  };
}

function normalizeDiscountRuleForState(rawRule = {}, index = 0) {
  const type = rawRule.type === 'percent' ? 'percent' : 'fixed';
  return {
    id: typeof rawRule.id === 'string' && rawRule.id.trim() ? rawRule.id.trim() : createEmptyDiscountRule(index).id,
    name: typeof rawRule.name === 'string' ? rawRule.name.trim() : '',
    status: rawRule.status === 'inactive' ? 'inactive' : 'active',
    type,
    amount: type === 'percent'
      ? (Number.isFinite(Number(rawRule.percentOff)) ? String(Number(rawRule.percentOff)) : '')
      : (Number.isFinite(Number(rawRule.amountMinor)) ? String(Number(rawRule.amountMinor) / 100) : ''),
    duration: type === 'percent' ? 'persistent' : 'credit',
    code: typeof rawRule.code === 'string' ? rawRule.code.trim().toUpperCase() : '',
    usageLimitTotal: Number.isFinite(Number(rawRule.usageLimitTotal)) && Number(rawRule.usageLimitTotal) > 0 ? String(Number(rawRule.usageLimitTotal)) : '',
    usageLimitPerMember: Number.isFinite(Number(rawRule.usageLimitPerMember)) && Number(rawRule.usageLimitPerMember) > 0 ? String(Number(rawRule.usageLimitPerMember)) : '',
    usageCount: Number(rawRule.usageCount || 0),
  };
}

export default function EditDiscountsForm({
  isOpen,
  teamId,
  onClose,
  onSave,
}) {
  const [discountRules, setDiscountRules] = useState([]);
  const [loadedDiscountRuleIds, setLoadedDiscountRuleIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !teamId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const rulesSnap = await getDocs(collection(db, `teams/${teamId}/membershipDiscounts`));
        if (cancelled) return;
        const loadedRules = rulesSnap.docs.map((docSnap, index) => normalizeDiscountRuleForState({
          id: docSnap.id,
          ...(docSnap.data() || {}),
        }, index));
        setDiscountRules(loadedRules);
        setLoadedDiscountRuleIds(loadedRules.map((rule) => rule.id));
      } catch (err) {
        if (!cancelled) setError('Failed to load membership discounts.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, teamId]);

  const addDiscountRule = () => {
    setDiscountRules((prev) => [...prev, createEmptyDiscountRule(prev.length)]);
  };

  const updateDiscountRule = (ruleId, patch) => {
    setDiscountRules((prev) => prev.map((rule) => {
      if (rule.id !== ruleId) return rule;
      const nextRule = { ...rule, ...patch };
      nextRule.duration = nextRule.type === 'percent' ? 'persistent' : 'credit';
      return nextRule;
    }));
  };

  const removeDiscountRule = (ruleId) => {
    const rule = discountRules.find((entry) => entry.id === ruleId);
    if (!window.confirm(`Remove "${rule?.name || rule?.code || 'this discount'}"? The discount will be deleted when you save.`)) return;
    setDiscountRules((prev) => prev.filter((rule) => rule.id !== ruleId));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const batch = writeBatch(db);
      const rulesToSave = discountRules.map((rule, index) => {
        const name = typeof rule.name === 'string' ? rule.name.trim() : '';
        const code = typeof rule.code === 'string' ? rule.code.trim().toUpperCase() : '';
        const type = rule.type === 'percent' ? 'percent' : 'fixed';
        const duration = type === 'percent' ? 'persistent' : 'credit';
        const status = rule.status === 'inactive' ? 'inactive' : 'active';
        const amountValue = Number(rule.amount);
        if (!name) throw new Error(`Discount ${index + 1} needs a name.`);
        if (!code) throw new Error(`Discount ${index + 1} needs a discount code.`);
        if (!Number.isFinite(amountValue) || amountValue <= 0) throw new Error(`Discount ${index + 1} needs a valid amount.`);
        if (type === 'percent' && amountValue > 100) throw new Error(`Percent discount ${index + 1} cannot exceed 100%.`);

        const payload = {
          name,
          status,
          type,
          duration,
          code,
          codeNormalized: code,
          updatedAt: serverTimestamp(),
        };
        if (type === 'percent') payload.percentOff = amountValue;
        else payload.amountMinor = Math.round(amountValue * 100);
        if (Number.isFinite(Number(rule.usageLimitTotal)) && Number(rule.usageLimitTotal) > 0) payload.usageLimitTotal = Math.round(Number(rule.usageLimitTotal));
        if (Number.isFinite(Number(rule.usageLimitPerMember)) && Number(rule.usageLimitPerMember) > 0) payload.usageLimitPerMember = Math.round(Number(rule.usageLimitPerMember));
        return { id: rule.id, payload };
      });

      rulesToSave.forEach((rule) => {
        batch.set(doc(db, `teams/${teamId}/membershipDiscounts/${rule.id}`), {
          ...rule.payload,
          ...(!loadedDiscountRuleIds.includes(rule.id) ? { createdAt: serverTimestamp(), usageCount: 0 } : {}),
        }, { merge: true });
      });
      loadedDiscountRuleIds
        .filter((ruleId) => !rulesToSave.find((rule) => rule.id === ruleId))
        .forEach((ruleId) => batch.delete(doc(db, `teams/${teamId}/membershipDiscounts/${ruleId}`)));

      await batch.commit();
      if (onSave) await onSave({
        discounts: {
          count: rulesToSave.length,
        },
      });
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save membership discounts.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <h2 className="modal-title">Membership Discounts</h2>
      {error && <ErrorMessage message={error} />}

      <form onSubmit={handleSave} className="modal-form">
        <p className="text-muted" style={{ marginTop: '-0.25rem' }}>
          Manage reusable membership discount codes here.
          Percent discounts persist on renewals. Fixed amounts become club credit and carry forward until used.
        </p>

        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {loading ? (
            <p className="text-muted">Loading membership discounts…</p>
          ) : discountRules.length === 0 ? (
            <p className="text-muted">No membership discounts set up yet.</p>
          ) : (
            discountRules.map((rule, index) => (
              <div key={rule.id} style={{ border: '1px solid #d1d5db', borderRadius: '0.75rem', padding: '0.85rem', display: 'grid', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
                  <strong>Discount {index + 1}</strong>
                  <button type="button" className="button button-secondary" onClick={() => removeDiscountRule(rule.id)} disabled={submitting}>
                    Remove
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: '0.75rem' }}>
                  <label className="form-group" style={{ marginBottom: 0 }}>
                    <span className="form-label">Name</span>
                    <input className="form-input" value={rule.name} onChange={(e) => updateDiscountRule(rule.id, { name: e.target.value })} disabled={submitting} />
                  </label>

                  <label className="form-group" style={{ marginBottom: 0 }}>
                    <span className="form-label">Code</span>
                    <input className="form-input" value={rule.code} onChange={(e) => updateDiscountRule(rule.id, { code: e.target.value.toUpperCase() })} placeholder="Required" disabled={submitting} />
                  </label>

                  <label className="form-group" style={{ marginBottom: 0 }}>
                    <span className="form-label">Status</span>
                    <select className="form-input" value={rule.status} onChange={(e) => updateDiscountRule(rule.id, { status: e.target.value })} disabled={submitting}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </label>

                  <label className="form-group" style={{ marginBottom: 0 }}>
                    <span className="form-label">Discount kind</span>
                    <select className="form-input" value={rule.type} onChange={(e) => updateDiscountRule(rule.id, { type: e.target.value })} disabled={submitting}>
                      <option value="percent">Persistent % off</option>
                      <option value="fixed">Credit amount</option>
                    </select>
                  </label>

                  <label className="form-group" style={{ marginBottom: 0 }}>
                    <span className="form-label">{rule.type === 'percent' ? 'Percent off' : 'Credit amount'}</span>
                    <input className="form-input" type="number" min="0" step={rule.type === 'percent' ? '1' : '0.01'} value={rule.amount} onChange={(e) => updateDiscountRule(rule.id, { amount: e.target.value })} disabled={submitting} />
                  </label>

                  <label className="form-group" style={{ marginBottom: 0 }}>
                    <span className="form-label">Behaviour</span>
                    <input
                      className="form-input"
                      value={rule.type === 'percent' ? 'Applies to each renewal' : 'Stored as club credit'}
                      disabled
                      readOnly
                    />
                  </label>

                  <label className="form-group" style={{ marginBottom: 0 }}>
                    <span className="form-label">Total uses limit</span>
                    <input className="form-input" type="number" min="0" step="1" value={rule.usageLimitTotal} onChange={(e) => updateDiscountRule(rule.id, { usageLimitTotal: e.target.value })} placeholder="Unlimited" disabled={submitting} />
                  </label>

                  <label className="form-group" style={{ marginBottom: 0 }}>
                    <span className="form-label">Per-member limit</span>
                    <input className="form-input" type="number" min="0" step="1" value={rule.usageLimitPerMember} onChange={(e) => updateDiscountRule(rule.id, { usageLimitPerMember: e.target.value })} placeholder="Unlimited" disabled={submitting} />
                  </label>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <span className="text-muted small">
                    Used {Number(rule.usageCount || 0)} time{Number(rule.usageCount || 0) === 1 ? '' : 's'}.
                  </span>
                </div>
              </div>
            ))
          )}

          <button type="button" className="button button-secondary" onClick={addDiscountRule} disabled={submitting}>
            Add discount
          </button>
        </div>

        <div className="form-actions">
          <SubmitButton type="button" label="Cancel" onClick={onClose} disabled={submitting} variant="ghost" size="sm" />
          <SubmitButton type="submit" label={submitting ? 'Saving…' : 'Save'} disabled={submitting || loading} variant="primary" />
        </div>
      </form>
    </>
  );
}

EditDiscountsForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  teamId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
};
